// ═══════════════════════════════════════════════════════════════
// speechApi.js — Service API centralisé pour ITRIIA Studio
// ═══════════════════════════════════════════════════════════════

const getToken = () => localStorage.getItem('oracle_token');

const headers = () => ({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
});

/**
 * Génère un audio TTS et retourne une blobUrl pour lecture
 */
export async function generateTTS(text, provider, voice, model) {
    const response = await fetch('/api/speech/tts', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ text, provider, voice, model }),
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
        throw new Error(err.error || err.details || `Erreur ${response.status}`);
    }

    const data = await response.json();
    return data.url;
}

/**
 * Récupère l'historique des générations
 */
export async function getHistory(type = null, limit = 50, offset = 0, search = null) {
    const params = new URLSearchParams({ limit, offset });
    if (type) params.set('type', type);
    if (search) params.set('search', search);

    const response = await fetch(`/api/speech/history?${params}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
    });

    if (!response.ok) throw new Error('Erreur récupération historique');
    return response.json();
}

/**
 * Supprimer une génération de l'historique
 */
export async function deleteGeneration(id) {
    const response = await fetch(`/api/speech/history/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` },
    });

    if (!response.ok) throw new Error('Erreur suppression');
    return response.json();
}

/**
 * Sauvegarder une transcription STT dans l'historique.
 * @param {string} text
 * @param {string} filename — nom fichier ou URL reel
 * @param {number} fileSize
 * @param {string} language
 * @param {object} [meta] — { provider, model, duration, source, sourceUrl }
 */
export async function saveSTTHistory(text, filename, fileSize, language, meta = {}) {
    const response = await fetch('/api/speech/history/stt', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
            text,
            filename,
            fileSize,
            language,
            provider: meta.provider,
            model: meta.model,
            duration: meta.duration,
            source: meta.source,
            sourceUrl: meta.sourceUrl,
        }),
    });
    return response.json();
}

/**
 * Exporter une génération vers un projet
 */
export async function exportToProject(projectId, generationId, exportType) {
    const response = await fetch('/api/speech/export', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ projectId, generationId, exportType }),
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
        throw new Error(err.error || err.details || `Erreur ${response.status}`);
    }

    return response.json();
}

/**
 * Récupère la liste des projets (pour le sélecteur export)
 */
export async function getProjects() {
    const response = await fetch('/api/projects', {
        headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    if (!response.ok) throw new Error('Erreur récupération projets');
    const data = await response.json();
    return data.projects || data || [];
}

/**
 * Récupère les voix disponibles pour un provider
 */
export async function getVoices(provider) {
    const response = await fetch(`/api/speech/voices?provider=${provider}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
    });

    if (!response.ok) throw new Error('Erreur récupération voix');

    const data = await response.json();
    return data.voices || [];
}

/**
 * Récupère le catalogue de modèles
 */
export async function getModels() {
    const response = await fetch('/api/speech/models', {
        headers: { 'Authorization': `Bearer ${getToken()}` },
    });

    if (!response.ok) throw new Error('Erreur récupération modèles');

    const data = await response.json();
    return data;
}

/**
 * Conversion vocale Speech-to-Speech (ElevenLabs)
 */
export async function convertSTS(file, voice, model, removeNoise = false) {
    const formData = new FormData();
    formData.append('file', file, file.name || 'audio.webm');
    formData.append('voice', voice);
    formData.append('model', model);
    if (removeNoise) formData.append('remove_background_noise', 'true');

    const response = await fetch('/api/speech/sts', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData,
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
        throw new Error(err.error || err.details || `Erreur ${response.status}`);
    }

    const data = await response.json();
    return data.url;
}
